import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Users, User, Baby, Link, Unlink, Search, Filter } from 'lucide-react';
import { parentAssignments, parentManagement, patients } from '../services/api';

const ParentAssignments = () => {
  const navigate = useNavigate();
  const { parentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [parent, setParent] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [availablePatients, setAvailablePatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [relationship, setRelationship] = useState('mother');
  const [isPrimary, setIsPrimary] = useState(false);

  useEffect(() => {
    fetchData();
  }, [parentId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch parent details
      const parentResponse = await parentManagement.getParentById(parentId);
      setParent(parentResponse.data.data);

      // Fetch current assignments
      const assignmentsResponse = await parentAssignments.getByParent(parentId);
      setAssignments(assignmentsResponse.data.data);

      // Fetch available patients (not already assigned to this parent)
      const patientsResponse = await patients.getAll();
      const allPatients = patientsResponse.data.data;
      const assignedPatientIds = assignmentsResponse.data.data.map(a => a.patientId);
      const available = allPatients.filter(p => !assignedPatientIds.includes(p.id));
      setAvailablePatients(available);

    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Falha ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssignment = async () => {
    if (!selectedPatient) {
      setError('Selecione uma criança');
      return;
    }

    try {
      setLoading(true);
      await parentAssignments.create({
        parentId: parentId,
        patientId: selectedPatient,
        relationship: relationship,
        isPrimary: isPrimary
      });

      setSuccess('Criança vinculada com sucesso');
      setShowAssignmentModal(false);
      setSelectedPatient('');
      setRelationship('mother');
      setIsPrimary(false);
      fetchData();
    } catch (error) {
      console.error('Error creating assignment:', error);
      setError(error.response?.data?.message || 'Falha ao vincular criança');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Tem certeza que deseja remover este vínculo?')) {
      return;
    }

    try {
      setLoading(true);
      await parentAssignments.delete(assignmentId);
      setSuccess('Vínculo removido com sucesso');
      fetchData();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      setError('Falha ao remover vínculo');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getRelationshipText = (relationship) => {
    switch (relationship) {
      case 'mother':
        return 'Mãe';
      case 'father':
        return 'Pai';
      case 'guardian':
        return 'Responsável';
      case 'grandmother':
        return 'Avó';
      case 'grandfather':
        return 'Avô';
      default:
        return relationship;
    }
  };

  if (loading && !parent) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard/parents')}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Gerenciar Crianças
            </h1>
            <p className="text-gray-600">
              {parent && `Vinculando crianças ao responsável: ${parent.firstName} ${parent.lastName}`}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowAssignmentModal(true)}
          className="bg-brand-700 text-white px-4 py-2 rounded-lg hover:bg-brand-800 flex items-center gap-2"
        >
          <Link className="w-4 h-4" />
          Vincular Criança
        </button>
      </div>

      {/* Notifications */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-800 border border-red-200 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 text-green-800 border border-green-200 rounded-lg">
          {success}
        </div>
      )}

      {/* Current Assignments */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Crianças Vinculadas ({assignments.length})
          </h2>
        </div>
        
        {assignments.length === 0 ? (
          <div className="p-8 text-center">
            <Baby className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma criança vinculada</h3>
            <p className="text-gray-600 mb-4">
              Vincule crianças a este responsável para começar
            </p>
            <button
              onClick={() => setShowAssignmentModal(true)}
              className="bg-brand-700 text-white px-4 py-2 rounded-lg hover:bg-brand-800"
            >
              Vincular Primeira Criança
            </button>
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Criança
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Idade
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Relacionamento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Principal
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {assignments.map((assignment) => (
                  <tr key={assignment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                            <Baby className="w-5 h-5 text-green-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {assignment.patient?.firstName} {assignment.patient?.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {assignment.patient?.patientId}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {calculateAge(assignment.patient?.dateOfBirth)} anos
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-brand-100 text-brand-800">
                        {getRelationshipText(assignment.relationship)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {assignment.isPrimary ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Sim
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          Não
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleDeleteAssignment(assignment.id)}
                        className="text-red-600 hover:text-red-900 flex items-center gap-1 ml-auto"
                      >
                        <Unlink className="w-4 h-4" />
                        Remover
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Vincular Criança
            </h3>
            
            <div className="space-y-4">
              {/* Patient Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecionar Criança *
                </label>
                <select
                  value={selectedPatient}
                  onChange={(e) => setSelectedPatient(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                >
                  <option value="">Selecione uma criança</option>
                  {availablePatients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.firstName} {patient.lastName} ({patient.patientId})
                    </option>
                  ))}
                </select>
              </div>

              {/* Relationship */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Relacionamento
                </label>
                <select
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                >
                  <option value="mother">Mãe</option>
                  <option value="father">Pai</option>
                  <option value="guardian">Responsável</option>
                  <option value="grandmother">Avó</option>
                  <option value="grandfather">Avô</option>
                </select>
              </div>

              {/* Primary Caregiver */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPrimary"
                  checked={isPrimary}
                  onChange={(e) => setIsPrimary(e.target.checked)}
                  className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                />
                <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-900">
                  Responsável principal
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAssignmentModal(false);
                  setSelectedPatient('');
                  setRelationship('mother');
                  setIsPrimary(false);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateAssignment}
                disabled={loading || !selectedPatient}
                className="bg-brand-700 text-white px-4 py-2 rounded-lg hover:bg-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Vinculando...' : 'Vincular'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentAssignments; 